import { FullContainer, Loading, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Tab, Tabs } from 'baseui/tabs-motion';
import { Key, useEffect, useState } from 'react';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { FLEET_DRIVER_PAGE, FLEET_DRIVERS_PAGE } from '../../../constants/fleet';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { getFleetDriver } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { fleetDriverReloadState } from '../../../storage/fleetDrivers';
import { FleetDriverDetail, FleetDriverTab } from '../../../types/drivers';
import { getDriverFullName, isFleetDriverTab } from '../../../utils/drivers';
import FleetDriverCurrentState from '../../fleet/drivers/driver/FleetDriverCurrentState';
import FleetDriverDetails from '../../fleet/drivers/driver/FleetDriverDetails';
import FleetDriverPhotos from '../../fleet/drivers/driver/FleetDriverPhotos';
import CustomBreadcrumbs from '../../shared/CustomBreadcrumbs';
import { StyledTooltip } from '../../ui/StyledTooltip';

function FleetDriverPage() {
  const [activeKey, setActiveKey] = useState<Key>(FleetDriverTab.general);
  const {
    overrides: { tabOverflowOverrides, tabsOverrides },
  } = useStyles();
  const { id: workspaceId, driverId, tab } = useParams();
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const apiParams = useRecoilValue(apiParamsState);
  const navigate = useNavigate();
  const redirectPath = workspaceId ? generatePath(FLEET_DRIVERS_PAGE.route, { id: workspaceId }) : '/';
  const [driver, setDriver] = useState<FleetDriverDetail>();
  const [reload, setReload] = useRecoilState(fleetDriverReloadState);

  useEffect(() => {
    if (tab && isFleetDriverTab(tab)) {
      setActiveKey(tab);
    } else {
      setActiveKey(FleetDriverTab.general);
    }
  }, [tab]);

  useEffect(() => {
    if (!driverId || !apiParams) return;

    const loadDriver = async () => {
      startLoading();
      const { driver: fleetDriver, error } = await getFleetDriver({
        clientId: apiParams.clientId,
        driverId,
        subClientId: apiParams.subClientId,
      });
      if (!error && fleetDriver) {
        setDriver(fleetDriver);
      } else {
        showFailFeedback(error || 'No se pudo cargar la información del conductor, inténtalo más tarde.');
        navigate(redirectPath, { replace: true });
      }
      stopLoading();
    };

    loadDriver();
  }, [driverId, apiParams, redirectPath, reload]);

  const updateUrl = (tab: Key) => {
    const route = generatePath(FLEET_DRIVER_PAGE.route, {
      id: workspaceId,
      driverId,
      tab: tab.toString(),
    });
    navigate(route, {
      replace: true,
    });
  };

  if (!workspaceId) {
    return (
      <Navigate
        to={redirectPath}
        replace
      />
    );
  }

  return (
    <FullContainer verticalFlex>
      {!driver && <Loading />}
      {!!driver && (
        <>
          <CustomBreadcrumbs
            historic={[
              {
                path: redirectPath,
                label: 'Conductores',
              },
              {
                label: getDriverFullName(driver) ?? '',
              },
            ]}
            actions={
              <StyledTooltip
                content="Recargar"
                showArrow={false}
              >
                <Button
                  size="compact"
                  shape="square"
                  onClick={() => {
                    setReload((prev) => prev + 1);
                  }}
                  kind="tertiary"
                  isLoading={loading}
                >
                  <MaterialIcon name="refresh" />
                </Button>
              </StyledTooltip>
            }
          />
          <Tabs
            activeKey={activeKey}
            onChange={({ activeKey }) => {
              updateUrl(activeKey);
            }}
            activateOnFocus
            overrides={tabsOverrides}
          >
            <Tab
              title="Generales"
              overrides={tabOverflowOverrides}
              key={FleetDriverTab.general}
            >
              <FleetDriverDetails driver={driver} />
            </Tab>
            <Tab
              title="Último Estado"
              overrides={tabOverflowOverrides}
              key={FleetDriverTab.currentState}
            >
              <FleetDriverCurrentState driver={driver} />
            </Tab>
            <Tab
              title="Fotos"
              overrides={tabOverflowOverrides}
              key={FleetDriverTab.photos}
            >
              <FleetDriverPhotos driverId={driver._id} />
            </Tab>
          </Tabs>
        </>
      )}
    </FullContainer>
  );
}

export default FleetDriverPage;
